// Port of src/app/components/nav-bar/dropdowns/filters/metadata-filter-dropdown/
// metadata-filter-dropdown.component.{ts,html,scss} (mypart-metadata-filter-dropdown).
//
// Same portal/backdrop/positioning strategy as FilterDropdown.tsx.
//
// ngOnInit deep-cloned the incoming filters array, dropped white-label-only
// groups for non-WL users, and synced subFilter states from selectedFilters.
// The clones are then passed straight into addOrChangeSubFilterSelectedFilters/
// removeFilter — exactly like Angular (updateFilter() swaps the clone into the
// main tree by title).

import { useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { Filter, SubFilter } from '../../interfaces/filter';
import { useFiltersStore } from '../../stores/filters-store';
import { useUserStore } from '../../stores/user-store';
import { useView } from '../view/use-view';
import { useIsEllipsis } from '../../shared/hooks/useIsEllipsis';
import { useOutsideClick } from '../../shared/hooks/useOutsideClick';
import './MetadataFilterDropdown.scss';

export interface MetadataFilterDropdownProps {
  filters: Filter[];
  isInputFilter?: boolean;
  position: CSSProperties;
  onFiltersClicked: () => void;
  onOutsideDropdownClick: () => void;
}

function MetadataSubFilterItem({
  filter,
  subFilter,
  onTextClicked,
  onToggle
}: {
  filter: Filter;
  subFilter: SubFilter;
  onTextClicked: (filter: Filter, subFilter: SubFilter) => void;
  onToggle: (filter: Filter, subFilter: SubFilter, state: 'green' | 'red') => void;
}) {
  const { ref } = useIsEllipsis<HTMLSpanElement>();

  return (
    <li className="mt sm:my-0">
      <div className="flex items-center rounded hover:bg-gray-200 dark:hover:bg-gray-600 filter-container">
        <span ref={ref}
              onClick={() => onTextClicked(filter, subFilter)}
              className={`dark:border-gray-600 cursor-pointer w-full py-2 mx-2 text-sm font-medium text-gray-800 rounded
                     max-[600px]:whitespace-normal dark:text-gray-300 ellipsis${subFilter.state === 'red' ? ' !text-red-600' : ''}${subFilter.state === 'green' ? ' !text-green-500' : ''}`}>{subFilter.name}
        </span>
        <div className="sm:hidden flex items-center justify-center mx-2 filter-actions">
          <svg onClick={() => onToggle(filter, subFilter, 'red')}
               aria-hidden="true"
               className={`cursor-pointer mr-6 sm:mr-4 w-6 h-5 sm:w-4 sm:h-3 text-gray-800 dark:text-gray-300
                      hover:text-black hover:dark:text-gray-50 active:text-red-600${subFilter.state === 'red' ? ' !text-red-600' : ''}`}
               fill="none"
               viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
            <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2" />
          </svg>

          <svg onClick={() => onToggle(filter, subFilter, 'green')}
               aria-hidden="true"
               className={`cursor-pointer w-6 h-6 sm:w-4 sm:h-4 text-gray-800 dark:text-gray-300 hover:text-black
                      hover:dark:text-gray-50 active:text-green-500${subFilter.state === 'green' ? ' !text-green-500' : ''}`}
               fill="none"
               viewBox="0 0 16 12" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 5.917 5.724 10.5 15 1.5" stroke="currentColor" strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2" />
          </svg>
        </div>
      </div>
    </li>
  );
}

export default function MetadataFilterDropdown({
  filters,
  isInputFilter = false,
  position,
  onFiltersClicked,
  onOutsideDropdownClick
}: MetadataFilterDropdownProps) {
  // Re-render on in-place filter-tree mutations.
  useFiltersStore(s => s.filtersVersion);
  const view = useView();

  // ngOnInit (runs once): deep-clone, white-label filtering, selectedFilters sync.
  const [localFilters] = useState<Filter[]>(() => {
    let cloned: Filter[] = JSON.parse(JSON.stringify(filters));

    if (!useUserStore.getState().isUserWhiteLabel) {
      cloned = cloned.filter((f) => !f.isWhiteLabel);
    }

    const selectedFilters = useFiltersStore.getState().selectedFilters;
    cloned.forEach((filter) => {
      if (Object.prototype.hasOwnProperty.call(selectedFilters, filter.title)) {
        const selectedFilter = selectedFilters[filter.title];
        filter.filters.forEach(subFilter => {
          const selectedSubFilter = selectedFilter.filters.find(sf => sf.id === subFilter.id);
          if (selectedSubFilter) {
            subFilter.state = selectedSubFilter.state;
          }
        });
      }
    });

    return cloned;
  });

  const panelRef = useOutsideClick<HTMLDivElement>(() => onOutsideDropdownClick());

  const emitFiltersClicked = () => {
    // Use a microtask
    Promise.resolve().then(() => {
      onFiltersClicked();
    });
  };

  const removeFilter = (filter: Filter, subFilter: SubFilter) => {
    useFiltersStore.getState().removeFilter(filter, subFilter);
  };

  const toggleFilter = (filter: Filter, subFilter: SubFilter, state: 'green' | 'red') => {
    if (subFilter.state === state) {
      removeFilter(filter, subFilter);
    } else {
      useFiltersStore.getState().addOrChangeSubFilterSelectedFilters(filter, subFilter, state, isInputFilter);
    }
    emitFiltersClicked();
  };

  const filterTextClicked = (filter: Filter, subFilter: SubFilter) => {
    if (subFilter.state === 'none') {
      toggleFilter(filter, subFilter, 'green');
    } else {
      toggleFilter(filter, subFilter, subFilter.state);
    }
  };

  return createPortal(
    <>
      <div className="overlay-backdrop" style={{ zIndex: 49 }} onClick={onOutsideDropdownClick} />
      <div className="metadata-filter-dropdown-host" style={position}>
        <div ref={panelRef}
             className={`w-[300px] max-h-[300px] overflow-y-auto mt-2 z-30 py-1 bg-white dark:bg-zinc-800 shadow-xl rounded-md overflow-hidden${view === 'home' ? ' !max-h-[260px]' : ''}`}>
          {localFilters.map((filter) => (
            <ul key={filter.title}
                aria-label={filter.title}
                className="px-3 pb-3 text-sm text-gray-700 dark:text-gray-200 overscroll-contain">
              {filter.filters.map((subFilter) => (
                <MetadataSubFilterItem key={subFilter.id}
                                       filter={filter}
                                       subFilter={subFilter}
                                       onTextClicked={filterTextClicked}
                                       onToggle={toggleFilter} />
              ))}
            </ul>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
}
